import CustomInputV2 from "@components/CustomInputV2/CustomInputV2";
import { Checkbox } from "@components/ui/checkbox";
import { Label } from "@components/ui/label";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { JobFormValues } from "../schema/jobForm.schema";
import { useHiringCompanies } from "../hooks/useHiringCompanies";
import CustomDropdown from "./CustomDropdown";
import FieldError from "./FieldError";

interface RequirementContextFormProps {
  // Imported drafts take the company and reference from the requirement.
  isImported?: boolean;
}

const HiringCompanyPicker = () => {
  const { control, setValue } = useFormContext<JobFormValues>();
  const hiringCompanies = useHiringCompanies();

  return (
    <Controller
      control={control}
      name="companyId"
      render={({ field, fieldState }) => (
        <>
          <CustomDropdown
            placeholder="Select hiring company"
            hasError={Boolean(fieldState.error)}
            value={field.value}
            options={hiringCompanies.map((item) => ({
              label: item.hiringCompany?.companyName,
              value: item.hiringCompany?._id,
            }))}
            onChange={(companyId) => {
              field.onChange(companyId);
              setValue(
                "companyName",
                hiringCompanies.find(
                  (item) => item.hiringCompany?._id === companyId,
                )?.hiringCompany?.companyName ?? "",
              );
            }}
          />
          <FieldError message={fieldState.error?.message} />
        </>
      )}
    />
  );
};

const RequirementContextForm = ({
  isImported,
}: RequirementContextFormProps) => {
  const {
    control,
    formState: { errors },
  } = useFormContext<JobFormValues>();
  const [companyName, requirementRefNo] = useWatch({
    control,
    name: ["companyName", "requirementRefNo"],
  });

  return (
    <div className="rounded-md border">
      <div className="flex items-center justify-between border-b px-4 py-2.5 bg-slate-50">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center rounded-full h-7 w-7 bg-slate-50 border text-xss font-bold text-light-black">
            02
          </div>
          <div className="flex flex-col">
            <span className="font-bold text-xs">Recruitment context</span>
            <span className="text-xss font-medium text-light-black">
              Confirm the hiring-company relationship attached to this draft.
            </span>
          </div>
        </div>

        <div className="rounded-full px-2 py-1 font-bold text-xsss text-gray-600 uppercase bg-gray-100">
          Agency record
        </div>
      </div>

      <div className="grid grid-cols-[0.9fr_0.9fr_1.2fr] gap-4 p-4">
        <div className="flex flex-col gap-1.5">
          <label className="text-xss font-bold text-slate-600">
            Hiring company
          </label>
          {isImported ? (
            <>
              <CustomInputV2
                readOnly
                placeholder="Company name"
                value={companyName}
                inputContainerStyle="bg-slate-50"
              />
              <FieldError message={errors.companyId?.message} />
            </>
          ) : (
            <HiringCompanyPicker />
          )}
          <span className="text-xsss text-light-black font-medium">
            {isImported
              ? "Selected from the partnered requirement library."
              : "Choose the partnered hiring company for this role."}
          </span>
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-xss font-bold text-slate-600">
            Hiring-company requirement
          </label>
          <CustomInputV2
            readOnly
            placeholder={isImported ? "Requirement code" : "Not linked"}
            value={isImported ? requirementRefNo : ""}
            inputContainerStyle="bg-slate-50"
          />
          <span className="text-xsss text-light-black font-medium">
            The source reference remains private to the recruitment workflow.
          </span>
        </div>
        <div className="flex flex-col rounded-md bg-slate-50 p-3 border gap-2">
          <label className="text-xsss font-bold text-slate-600 uppercase tracking-wider">
            Company-name visibility
          </label>
          <div className="flex items-start gap-2.5">
            <Controller
              control={control}
              name="showCompanyDetails"
              render={({ field }) => (
                <Checkbox
                  id="hiring-company-visibility"
                  checked={field.value}
                  onCheckedChange={(checked) =>
                    field.onChange(checked === true)
                  }
                />
              )}
            />
            <div className="flex flex-col gap-2 mt-0.5 cursor-pointer">
              <Label
                htmlFor="hiring-company-visibility"
                className="text-xss font-semibold text-black"
              >
                Show the hiring company on the candidate job post
              </Label>
              <span className="text-xsss text-light-black font-medium">
                Turn this off for a confidential search. The requirement
                reference is never published; review the job copy for other
                identifying detail.
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RequirementContextForm;
